import React, { useEffect, useRef, useState } from 'react';
import { X, Send, Image as ImageIcon, Headphones } from 'lucide-react';
import { supportApi, type SupportMessage } from '../../services/supportApi';
import { supportSocket } from '../../services/supportSocket';
import { buildStaticUrl } from '../../config/env';
import { useStore } from '../../stores';

interface SupportChatModalProps {
  conversationId: string;
  onClose: () => void;
}

/**
 * 客服聊天窗口
 * 连接 /support 实时通道，加载历史、收发文本/图片。
 */
export const SupportChatModal: React.FC<SupportChatModalProps> = ({ conversationId, onClose }) => {
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [input, setInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useStore();
  const userAvatar = user?.avatar ? buildStaticUrl(user.avatar) : '';
  const userInitial = (user?.nickname || '我').charAt(0).toUpperCase();

  useEffect(() => {
    let mounted = true;

    const upsert = (msg: SupportMessage) => {
      if (!mounted) return;
      setMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
    };

    // 先拉一次历史（REST），再建立实时连接
    supportApi.getMessages(conversationId).then((history) => {
      if (mounted) setMessages(history);
    });

    supportSocket.connect();
    supportSocket.onConnect(() => {
      supportSocket.join(conversationId);
    });
    supportSocket.onMessage(upsert);
    supportSocket.onHistory((data) => {
      if (data.conversationId === conversationId && mounted) {
        setMessages(data.messages);
      }
    });

    // 若已连接，直接 join
    if (supportSocket.isConnected()) {
      supportSocket.join(conversationId);
    }

    return () => {
      mounted = false;
      supportSocket.disconnect();
    };
  }, [conversationId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    supportSocket.sendText(conversationId, text);
    setInput('');
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const imageUrl = await supportApi.uploadImage(file);
      supportSocket.sendImage(conversationId, imageUrl);
    } catch {
      // 忽略，保持简单
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white dark:bg-gray-800 w-full sm:max-w-md h-[80vh] sm:h-[600px] rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between px-4 h-14 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">联系客服</h3>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 消息列表 */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {messages.map((m) => {
            const isUser = m.sender_role === 'user';

            const avatar = isUser ? (
              userAvatar ? (
                <img
                  src={userAvatar}
                  alt="我"
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary-500 text-white text-xs flex items-center justify-center flex-shrink-0">
                  {userInitial}
                </div>
              )
            ) : (
              <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-200 flex items-center justify-center flex-shrink-0">
                <Headphones className="w-4 h-4" />
              </div>
            );

            // 客服消息靠右，用户消息靠左
            const alignRight = !isUser;

            return (
              <div
                key={m.id}
                className={`flex items-end gap-2 ${alignRight ? 'justify-end' : 'justify-start'}`}
              >
                {!alignRight && avatar}
                <div
                  className={`max-w-[70%] rounded-2xl px-3 py-2 text-sm ${
                    alignRight
                      ? 'bg-primary-500 text-white rounded-br-sm'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-bl-sm'
                  }`}
                >
                  {m.content_type === 'image' && m.image_url ? (
                    <img
                      src={buildStaticUrl(m.image_url)}
                      alt="图片消息"
                      className="rounded-lg max-w-full max-h-60 object-contain"
                    />
                  ) : (
                    <span className="whitespace-pre-wrap break-words">{m.content}</span>
                  )}
                </div>
                {alignRight && avatar}
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* 输入区 */}
        <div className="flex items-center gap-2 px-3 h-16 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="p-2 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
            title="发送图片"
          >
            <ImageIcon className="w-5 h-5" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleUpload}
          />
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="输入消息…"
            className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <button
            onClick={handleSend}
            className="p-2 rounded-md bg-primary-500 text-white hover:bg-primary-600"
            title="发送"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};
